'use client';

import dynamic from 'next/dynamic';
import { useId } from 'react';

import { LOCATION_PIN_MD_ONLY_ATTR } from '@/constants/locationPin';
import { useDeferredMount } from '@/hooks/useDeferredMount';

import styles from './LocationPin.module.css';
import { LocationPinMark, PIN_HEIGHT, PIN_WIDTH } from './LocationPinMark';
import { LocationPinWiggle } from './LocationPinWiggle';

import type { LocationPinMotionProps } from './LocationPinMotion';

const LocationPinMotionDynamic = dynamic(
  () => import('./LocationPinMotion').then((mod) => mod.LocationPinMotion),
  { ssr: false },
);

export type LocationPinProps = LocationPinMotionProps & {
  /**
   * When true, hide below Tailwind `md` (Figma UI Mobile: chapter header
   * bodies omit pins; desktop keeps them). Mid-section prose blocks leave this
   * false.
   */
  mdOnly?: boolean;
};

function LocationPinStatic({
  color,
  size = PIN_WIDTH,
  className,
  mdOnly = false,
}: Omit<LocationPinProps, 'animateOnView'>) {
  const clipId = useId();
  const height = Math.round(size * (PIN_HEIGHT / PIN_WIDTH));
  const style = color
    ? ({ '--pin-color': color, '--pin-tint': color } as React.CSSProperties)
    : undefined;

  return (
    <span
      aria-hidden
      className={[styles.root, mdOnly ? styles.mdOnly : null, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      {...(mdOnly ? { [LOCATION_PIN_MD_ONLY_ATTR]: '' } : {})}
    >
      <LocationPinWiggle>
        <LocationPinMark clipId={clipId} width={size} height={height} />
      </LocationPinWiggle>
    </span>
  );
}

/**
 * Chapter-coloured location pin. Motion spring is deferred until near the
 * viewport or idle so decorative Motion hydration is delayed. GSAP-owned pins
 * (`animateOnView={false}`) stay static. A CSS `prefers-reduced-motion` twin
 * paints the settled pin immediately (no empty-slot flash / CLS).
 */
export function LocationPin({
  color,
  size = PIN_WIDTH,
  className,
  animateOnView = true,
  mdOnly = false,
}: LocationPinProps) {
  const reducedClipId = useId();
  const { ref, ready } = useDeferredMount<HTMLSpanElement>({
    enabled: animateOnView,
  });
  const height = Math.round(size * (PIN_HEIGHT / PIN_WIDTH));
  const style = color
    ? ({
        '--pin-color': color,
        '--pin-tint': color,
        inlineSize: size,
        blockSize: height,
      } as React.CSSProperties)
    : ({ inlineSize: size, blockSize: height } as React.CSSProperties);

  if (!animateOnView) {
    return (
      <LocationPinStatic
        color={color}
        size={size}
        className={className}
        mdOnly={mdOnly}
      />
    );
  }

  return (
    <span
      ref={ref}
      aria-hidden
      className={[styles.root, mdOnly ? styles.mdOnly : null, className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      {...(mdOnly ? { [LOCATION_PIN_MD_ONLY_ATTR]: '' } : {})}
    >
      <span className={styles.reducedStatic}>
        <LocationPinWiggle>
          <LocationPinMark
            clipId={reducedClipId}
            width={size}
            height={height}
          />
        </LocationPinWiggle>
      </span>
      <span className={styles.motionSlot}>
        {ready ? (
          <LocationPinMotionDynamic
            color={color}
            size={size}
            animateOnView={animateOnView}
          />
        ) : null}
      </span>
    </span>
  );
}
